import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import toast from "react-hot-toast"
import { FaSearch } from "react-icons/fa"

import { usePostAddress } from "../../hooks/useAddresses"
import useModalStore from "../../stores/ModalStore"
import SelectAdvanced from "../../components/SelectAdvanced/SelectAdvanced"
import Spinner from "../../components/Spinner/Spinner"
import { COUNTRIES } from "../../utils/utils"
import localConfig from "../../local_config"

import "./AddAddressModal.css"

declare global {
    interface Window {
        google: any
    }
}

interface AddAddressModalProps {
    /** Optional func to execute when modal is closed */
    onClose?: () => void
}

const AddAddressModal = ({ onClose }: AddAddressModalProps) => {

    const searchInputRef = useRef<HTMLInputElement>(null)

    const defaultCountryCode = COUNTRIES.hasOwnProperty(localConfig.countryCode) ? localConfig.countryCode : "PE"

    const { t } = useTranslation("common")

    const { mutateAsync: postUserAddress } = usePostAddress()

    const { closeModal } = useModalStore((state) => ({
        closeModal: state.closeModal,
    }))

    const [isLoading, setIsLoading] = useState(false)
    const [isFakeSearching, setIsFakeSearching] = useState(false)
    const [formData, setFormData] = useState({
        street: "",
        city: "",
        state: "",
        zip_code: "",
        // NOTE this is hard coded to prevent double update in select advanced when searching the address
        country: COUNTRIES[defaultCountryCode].name,
        country_code: defaultCountryCode
    })


    const isFormValid = Object.values(formData).every((val) => val.trim() !== "")

    const handleInputChange = (field: keyof typeof formData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
    }

    const handleZipCodeChange = (zip_code: string, countryCode: string) => {
        if (!countryCode) {
            toast.error(t("country_missing"))
            return
        }
        if (!zip_code) {
            setFormData(prev => ({ ...prev, zip_code: "" }))
            return
        }

        let isValid = false
        let formattedZip = zip_code

        switch (countryCode) {
            case "PE": // Peru
                isValid = /^\d{0,5}$/.test(zip_code)
                break
                
            case "CO": // Colombia
                isValid = /^\d{0,6}$/.test(zip_code)
                break
                
            case "BR": // Brazil
                isValid = /^\d{0,8}$/.test(zip_code)
                break
                
            case "MX": // Mexico
                isValid = /^\d{0,5}$/.test(zip_code)
                break
                
            case "AR": // Argentina
                isValid = /^([A-Za-z]\d{0,4}[A-Za-z]{0,3}|\d{0,4})$/.test(zip_code)
                break
                
            case "CL": // Chile
                isValid = /^\d{0,7}$/.test(zip_code)
                break
                
            case "BO": // Bolivia
                isValid = /^\d{0,4}$/.test(zip_code)
                break
                
            case "SV": // El Salvador
                isValid = zip_code === '1101'
                break
                
            default:
                toast.error(t("country_not_supported"))
                isValid = false
        }

        if (isValid) {
            setFormData(prev => ({ ...prev, zip_code: formattedZip }))
        }
    }

    const handleCountryChange = (selectedCountry: string) => {
        if (selectedCountry) {
            // Reset values if country is changed
            if (formData.country_code !== "" && formData.country !== "" && formData.street !== "") {
                setFormData(prev => ({
                    ...prev,
                    street: "",
                    city: "",
                    state: "",
                    zip_code: "",
                }))
                if (searchInputRef.current) {
                    searchInputRef.current.value = ""
                }
            }
            handleInputChange("country_code", selectedCountry)
            handleInputChange("country", COUNTRIES[selectedCountry]?.name)
        }
    }

    const handleFakeSearch = () => {
        if (searchInputRef.current) {
            searchInputRef.current.focus()
        }
        setIsFakeSearching(true)
        setTimeout(() => {
            setIsFakeSearching(false)
        }, 500)
    }

    const handleClose = () => {
        closeModal()
        if (onClose) onClose()
    }

    const handleSubmit = async () => {
        try {
            setIsLoading(true)
            const result = await postUserAddress(formData)
            
            setIsLoading(false)
            if (result.status === 201) {
                toast.dismiss()
                toast.success(t("user_address.address_added"))
                closeModal()
                if (onClose) onClose()
            }
        } catch (e: any) {
            setIsLoading(false)
            if (e?.response?.data?.error) {
                toast.dismiss()
                toast.error(t("user_address.too_many_addresses"))
            } else {
                toast.dismiss()
                toast.error(t("user_address.unexpected_error"))
            }   
        }
    }

    useEffect(() => {
        if (!window.google || !searchInputRef.current) return

        const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
            types: ["address"],
        })

        autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace()
            if (!place || !place.address_components) return

            const getAddressComponent = (type: string, key: "long_name" | "short_name" = "long_name") => {
                const c = place.address_components.find((comp: any) => comp.types.includes(type))
                return c ? c[key] : ""
            }

            const streetNumber = getAddressComponent("street_number")
            const route = getAddressComponent("route")
            const city = getAddressComponent("locality")
            const state = getAddressComponent("administrative_area_level_1")
            const zip_code = getAddressComponent("postal_code")
            // const country = getAddressComponent("country")
            const country_code = getAddressComponent("country", "short_name") // NOTE this doesnt need to be an input
            
            if (COUNTRIES.hasOwnProperty(country_code)) {
                handleCountryChange(country_code)
                setFormData(prev => ({
                    ...prev,
                    street: `${streetNumber} ${route}`.trim(),
                    city,
                    state,
                    zip_code,
                }))
            } else {
                toast.error(t("country_not_supported"))
            }
        })
    }, [])

    return (
        <div className="add-address-modal">
            <div className="create-address-title">{t("user_address.title")}</div>
            <div className="address-field">
                <div>{t("user_address.search_address_label")}</div>
                <div className="search-address-field">
                    <input
                        ref={searchInputRef}
                        type="text"
                        className="search-address-field-input"
                        placeholder={t("user_address.search_address_placeholder")}
                    />
                    {isFakeSearching ? (
                        <Spinner sizeMultiplier={0.5} className="search-address-icon"/>
                    ) : (
                        <FaSearch className="search-address-icon" onClick={() => handleFakeSearch()}/>
                    )}
                    
                </div>
            </div>
            <SelectAdvanced
                label={t("user_address.country_label")}
                value={formData?.country_code || ""}
                items={COUNTRIES}
                image={false}
                onChange={handleCountryChange}
            />
            <div className="address-field-container">
                <div className="address-field">
                    <div>{t("user_address.street_label")}</div>
                    <input
                        className="address-field-value"
                        value={formData.street}
                        onChange={(e) => handleInputChange("street", e.target.value)}
                        placeholder={t("user_address.street_placeholder")}
                    />
                </div>
                <div className="address-field">
                    <div>{t("user_address.city_label")}</div>
                    <input
                        className="address-field-value"
                        value={formData.city}
                        onChange={(e) => handleInputChange("city", e.target.value)}
                        placeholder={t("user_address.city_placeholder")}
                    />
                </div>
                <div className="address-field">
                    <div>{t("user_address.state_label")}</div>
                    <input
                        className="address-field-value"
                        value={formData.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                        placeholder={t("user_address.state_placeholder")}
                    />
                </div>
                <div className="address-field">
                    <div>{t("user_address.zip_code_label")}</div>
                    <input
                        className="address-field-value"
                        value={formData.zip_code}
                        onChange={(e) => handleZipCodeChange(e.target.value, formData?.country_code)}
                        placeholder={t("user_address.zip_code_placeholder")}
                    />
                </div>
            </div>
            <div className="address-action-button-div">
                {isLoading ? (
                    <Spinner />
                ) : (
                    <>
                        <button className="address-action-button" onClick={handleClose}>
                            {t("cancel")}
                        </button>
                        <button className="address-action-button" disabled={!isFormValid} onClick={handleSubmit}>
                            {t("user_address.submit")}
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}

export default AddAddressModal
